import React, { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    Paper,
    Stack,
    Divider,
    Avatar,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    alpha,
    Button,
    Tooltip,
    IconButton,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RoomIcon from '@mui/icons-material/Room';
import PersonIcon from '@mui/icons-material/Person';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IEventSession } from '../../interfaces/IEventSession';
import { ILocationList } from '../../interfaces/ILocationList';
import { IDelegateRole } from '../../interfaces/IDelegateRole';
import { IEventSessionType } from '../../interfaces/IEventSessionType';
import { formatTimeRange } from '../../utils/formatters';
import { IDelegate } from '../../interfaces/IDelegate';
import { SessionMode } from '../../enums/SessionMode';
import { VirtualPlatform } from '../../enums/VirtualPlatform';

interface EventSessionItemProps {
    session: IEventSession;
    location: ILocationList | undefined;
    eventTypes: IEventSessionType[];
    delegateRoles: IDelegateRole[];
    delegates: IDelegate[];
    onDelegateClick?: (delegate: IDelegate, roleId?: string) => void;
    dayId: string;
    primaryColor?: string;
    secondaryColor?: string;
}

const EventSessionItem: React.FC<EventSessionItemProps> = ({
    session,
    location,
    eventTypes,
    delegateRoles,
    delegates,
    onDelegateClick,
    dayId,
    primaryColor = '#493193',
    secondaryColor = '#2fadd8'
}) => {

    const platformLabel = (platform?: VirtualPlatform): string => {
        switch (platform) {
            case VirtualPlatform.Zoom: return 'Zoom';
            case VirtualPlatform.MicrosoftTeams: return 'Microsoft Teams';
            case VirtualPlatform.GoogleMeet: return 'Google Meet';
            case VirtualPlatform.WebEx: return 'WebEx';
            default: return 'Online';
        }
    };

    const getEventTypeName = (typeId: string): string => {
        const eventType = eventTypes.find(eventType => eventType.id === typeId);
        return eventType ? eventType.name : 'Unknown Event Type';
    };

    const getDelegateRoleName = (roleId: string): string => {
        const role = delegateRoles.find(role => role.id === roleId);
        return role ? role.name : 'Unknown Role';
    };

    const assignedDelegates = session.sessionDelegates?.map(delegate => {
        const foundDelegate = delegates.find(d => d.id === delegate.delegateId);
        if (foundDelegate) {
            return {
                delegate: foundDelegate,
                name: `${foundDelegate.firstname} ${foundDelegate.lastname}`,
                roleId: delegate.roleId,
                description: delegate.description,
                imageUrl: foundDelegate.imageUrl
            };
        }
        return null;
    }).filter(Boolean) as { delegate: IDelegate; name: string; roleId: string; description: string, imageUrl?: string }[] | undefined;

    const room = location?.rooms.find(room => room.id === session.roomId);
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const copyToClipboard = (value: string, field: string) => {
        navigator.clipboard.writeText(value);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    return (
        <Paper
            elevation={1}
            sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(primaryColor, 0.1)}`,
                boxShadow: `0 2px 8px ${alpha(primaryColor, 0.05)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(primaryColor, 0.15)}`,
                    borderColor: alpha(primaryColor, 0.2),
                },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 600,
                            color: primaryColor,
                        }}
                    >
                        {session.title}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <AccessTimeIcon fontSize="small" sx={{ color: primaryColor }} />
                        <Typography variant="body2" color="text.secondary">
                            {formatTimeRange(session.startTime, session.endTime)}
                        </Typography>
                    </Stack>

                    {room && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <RoomIcon fontSize="small" sx={{ color: primaryColor }} />
                            <Typography variant="body2" color="text.secondary">
                                {room.name}
                            </Typography>
                        </Stack>
                    )}
                </Box>

                {session.virtualMeetingUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: { xs: 1.5, sm: 0 }, flexShrink: 0 }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<VideocamOutlinedIcon />}
                            endIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem !important' }} />}
                            href={session.virtualMeetingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                bgcolor: primaryColor,
                                color: '#fff',
                                fontWeight: 600,
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: `0 2px 8px ${alpha(primaryColor, 0.35)}`,
                                '&:hover': {
                                    bgcolor: primaryColor,
                                    filter: 'brightness(1.12)',
                                    boxShadow: `0 4px 14px ${alpha(primaryColor, 0.5)}`,
                                },
                            }}
                        >
                            Join {platformLabel(session.virtualPlatform)}
                        </Button>
                    </Box>
                )}
            </Box>
            <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                        label={getEventTypeName(session.sessionTypeId ?? '')}
                        sx={{
                            fontWeight: 500,
                            borderRadius: '50px',
                            bgcolor: alpha(primaryColor, 0.08),
                            color: primaryColor,
                            border: `1px solid ${alpha(primaryColor, 0.2)}`,
                        }}
                    />
                    {session.sessionMode === SessionMode.Virtual && (
                        <Chip
                            icon={<VideocamOutlinedIcon sx={{ fontSize: '1rem' }} />}
                            label="Virtual"
                            size="small"
                            sx={{
                                fontWeight: 500,
                                borderRadius: '50px',
                                bgcolor: alpha('#2fadd8', 0.1),
                                color: '#2fadd8',
                                border: '1px solid rgba(47,173,216,0.25)',
                            }}
                        />
                    )}
                </Stack>
            </Box>

            <Typography variant="body1" component="p" sx={{
                lineHeight: 1.7, color: 'text.secondary', wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap' }}>
                {session.description}
            </Typography>

            {(session.virtualMeetingId || session.virtualMeetingPassword || session.virtualAccessInstructions) && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={1.5}>
                        {(session.virtualMeetingId || session.virtualMeetingPassword) && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                {session.virtualMeetingId && (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            bgcolor: alpha('#2fadd8', 0.06),
                                            border: '1px solid rgba(47,173,216,0.2)',
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1 }}>Meeting ID</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                                                {session.virtualMeetingId}
                                            </Typography>
                                        </Box>
                                        <Tooltip title={copiedField === 'id' ? 'Copied!' : 'Copy'}>
                                            <IconButton size="small" onClick={() => copyToClipboard(session.virtualMeetingId!, 'id')}>
                                                {copiedField === 'id' ? <CheckIcon fontSize="small" sx={{ color: 'success.main' }} /> : <ContentCopyIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                                {session.virtualMeetingPassword && (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            bgcolor: alpha('#2fadd8', 0.06),
                                            border: '1px solid rgba(47,173,216,0.2)',
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1 }}>Password</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                                                {session.virtualMeetingPassword}
                                            </Typography>
                                        </Box>
                                        <Tooltip title={copiedField === 'pw' ? 'Copied!' : 'Copy'}>
                                            <IconButton size="small" onClick={() => copyToClipboard(session.virtualMeetingPassword!, 'pw')}>
                                                {copiedField === 'pw' ? <CheckIcon fontSize="small" sx={{ color: 'success.main' }} /> : <ContentCopyIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Stack>
                        )}
                        {session.virtualAccessInstructions && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    bgcolor: alpha(primaryColor, 0.04),
                                    border: `1px solid ${alpha(primaryColor, 0.15)}`,
                                    borderLeft: `3px solid ${primaryColor}`,
                                    borderRadius: 2,
                                    p: 1.5,
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" sx={{ color: primaryColor, flexShrink: 0, mt: '2px' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                    {session.virtualAccessInstructions}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </>
            )}

            {assignedDelegates && assignedDelegates.length > 0 && (
                <>
                    <Divider sx={{ my: 2 }} />

                    <Typography 
                        variant="subtitle1" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 600,
                            color: primaryColor,
                        }}
                    >
                        {assignedDelegates.length === 1 ? 'Delegate' : 'Delegates'}
                    </Typography>

                    <List sx={{ py: 0 }}>
                        {assignedDelegates.map((del, index) => {
                            const content = <>
                                <ListItemAvatar>
                                    <Avatar 
                                        src={del.imageUrl}
                                        alt={del.name}
                                        title={del.name}
                                        sx={{
                                            border: `2px solid ${alpha(primaryColor, 0.2)}`,
                                        }}
                                    >
                                        <PersonIcon sx={{ color: primaryColor }} />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                                            {del.name}
                                        </Typography>
                                    }
                                    secondary={
                                        <React.Fragment>
                                            <Typography
                                                sx={{ display: 'inline' }}
                                                component="span"
                                                variant="body2"
                                                color="text.primary"
                                            >
                                                {getDelegateRoleName(del.roleId)}
                                            </Typography>
                                            {del.description && ` · ${del.description}`}
                                        </React.Fragment>
                                    }
                                />
                            </>;
                            return (
                                <ListItem key={`${del.delegate.id}-${del.roleId}-${index}`} alignItems="flex-start" sx={{ px: 0 }}>
                                    {onDelegateClick && del.delegate.id ? (
                                        <ListItemButton
                                            component="button"
                                            type="button"
                                            aria-label={`View ${del.name}'s profile`}
                                            aria-haspopup="dialog"
                                            onClick={event => {
                                                event.stopPropagation();
                                                onDelegateClick(del.delegate, del.roleId);
                                            }}
                                            sx={{
                                                width: '100%', minWidth: 0, minHeight: 'var(--touch-min)', px: 0,
                                                textAlign: 'left', alignItems: 'flex-start', color: 'text.primary',
                                                '& .MuiListItemText-root': { minWidth: 0, overflowWrap: 'anywhere' },
                                                '&:hover .MuiListItemText-primary, &.Mui-focusVisible .MuiListItemText-primary': { textDecoration: 'underline' },
                                            }}
                                        >
                                            {content}
                                        </ListItemButton>
                                    ) : content}
                                </ListItem>
                            );
                        })}
                    </List>
                </>
            )}
        </Paper>
    );
};

export default EventSessionItem;